import type { SaleStatus } from "./state.js";
import type { SalePayload } from "./sale.js";

// The platform layer behind the core (design section 2.6): Windows implements these in the agent; Android and iOS
// will implement them in their apps. Every method is async so a platform can use an asynchronous database.

export interface StoredSale {
  saleId: string;
  deviceSeq: number;
  grantId: string | null;
  deviceTime: string;
  clockOffsetMs: number;
  payload: SalePayload;
  payloadHash: string;
  status: SaleStatus;
  attempts: number;
  nextAttemptAt: number;
  lastError: string | null;
  serverId: string | null;
  reviewReasons: string[];
}

export interface NewSale {
  saleId: string;
  grantId: string | null;
  deviceTime: string;
  clockOffsetMs: number;
  payload: SalePayload;
  payloadHash: string;
}

export type SaveResult =
  | { kind: "saved"; sale: StoredSale }
  /** The same sale id was saved before with the same payload: nothing new is written. */
  | { kind: "existing"; sale: StoredSale }
  /** The same sale id was saved before with a different payload: refused, the stored sale is unchanged. */
  | { kind: "conflict"; sale: StoredSale };

/** Durable sale storage. `saveSale` assigns the next device sequence number and stores the sale in one transaction. */
export interface SaleStore {
  saveSale(s: NewSale): Promise<SaveResult>;
  getSale(saleId: string): Promise<StoredSale | null>;
  /**
   * Pending sales due for an upload at `now`, lowest device sequence first. Stops at the first pending sale that is
   * not yet due, so a later sale never overtakes one that is waiting for its retry.
   */
  duePending(now: number, limit: number): Promise<StoredSale[]>;
  markSynced(saleId: string, serverId: string, reviewReasons: string[]): Promise<void>;
  markFailed(saleId: string, code: string): Promise<void>;
  scheduleRetry(saleId: string, attempts: number, nextAttemptAt: number, error: string): Promise<void>;
  requestRetry(saleId: string): Promise<StoredSale>;
}

/** Signing of the e-invoice (design sections 2.6 and 11.3). Not implemented in this phase. */
export interface Signer {
  readonly available: boolean;
  /** Why signing is unavailable, for the register and the logs. */
  readonly reason: string;
}

/** The only signer of this phase: it never signs. A validated signer replaces it once design blocker P1b is closed. */
export const SIGNING_UNAVAILABLE: Signer = Object.freeze({
  available: false,
  reason: "Signing is not implemented: the signing architecture has not been validated (offline POS design, section 2.6)."
});

/** Receipt printing (design section 12). Not implemented until it is tested on the actual printers. */
export interface Printer {
  readonly available: boolean;
  readonly reason: string;
}

export const PRINTING_UNAVAILABLE: Printer = Object.freeze({
  available: false,
  reason: "Printing is not implemented until it is tested on the actual printers (offline POS design, section 12)."
});
